/**
 * Maintenance Requirements Wrapper
 * Provides backwards compatibility while supporting multi-country regulations
 * This file replaces the original maintenanceRequirements.js
 */

import { loadCountryRegulations, getCurrentCountry } from './regulations/regulationLoader';

// Store loaded regulations
let currentRegulations = null;
let loadPromise = null;

/**
 * Initialize regulations (called once at app startup)
 */
async function initializeRegulations() {
  if (loadPromise) return loadPromise;
  
  loadPromise = (async () => {
    const country = getCurrentCountry();
    currentRegulations = await loadCountryRegulations(country);
    return currentRegulations;
  })();
  
  return loadPromise;
}

/**
 * Get maintenance schedule for a vessel
 * Maintains exact same interface as before
 */
export async function getMaintenanceSchedule(vessel) {
  // Ensure regulations are loaded
  if (!currentRegulations) {
    await initializeRegulations();
  }
  
  // Call the country-specific function
  return currentRegulations.getMaintenanceSchedule(vessel);
}

/**
 * Calculate maintenance due dates
 * Maintains exact same interface as before
 */
export async function calculateMaintenanceDue(vessel, lastMaintenance = {}) {
  // Ensure regulations are loaded
  if (!currentRegulations) {
    await initializeRegulations();
  }
  
  return currentRegulations.calculateMaintenanceDue(vessel, lastMaintenance);
}

/**
 * Get maintenance categories
 */
export async function getMaintenanceCategories() {
  if (!currentRegulations) {
    await initializeRegulations();
  }
  
  return currentRegulations.MAINTENANCE_CATEGORIES || {
    LSA: 'Life Saving Appliances',
    FFE: 'Fire Fighting Equipment',
    MACHINERY: 'Machinery & Systems',
    ELECTRICAL: 'Electrical Systems',
    NAVIGATION: 'Navigation Equipment',
    RADIO: 'Radio Equipment',
    MEDICAL: 'Medical Equipment',
    SPECIAL_OPS: 'Special Operations',
    SURVEYS: 'Surveys & Inspections'
  };
}

/**
 * Switch to different country regulations
 */
export async function switchCountry(countryCode) {
  currentRegulations = await loadCountryRegulations(countryCode);
  
  // Dispatch event for UI updates
  window.dispatchEvent(new CustomEvent('maintenanceRegulationsChanged', { 
    detail: { country: countryCode } 
  }));
  
  return currentRegulations;
}

// For synchronous imports (gradually phase these out)
export const MAINTENANCE_CATEGORIES = {
  LSA: 'Life Saving Appliances',
  FFE: 'Fire Fighting Equipment',
  MACHINERY: 'Machinery & Systems',
  ELECTRICAL: 'Electrical Systems',
  NAVIGATION: 'Navigation Equipment',
  RADIO: 'Radio Equipment',
  MEDICAL: 'Medical Equipment',
  SPECIAL_OPS: 'Special Operations',
  SURVEYS: 'Surveys & Inspections'
};

// FT-267 §16: canonical category codes + display labels for unified storage and filter.
// `value` stored on records is always the canonical lowercase code (e.g. 'lsa').
// `label` is the user-facing display string (used in dropdowns + detail view).
export const CATEGORY_DEFINITIONS = [
  { value: 'surveys',     label: 'Surveys & Inspections' },
  { value: 'lsa',         label: 'Life Saving Appliances' },
  { value: 'ffe',         label: 'Fire Fighting Equipment' },
  { value: 'machinery',   label: 'Machinery & Systems' },
  { value: 'electrical',  label: 'Electrical Systems' },
  { value: 'navigation',  label: 'Navigation Equipment' },
  { value: 'radio',       label: 'Radio Equipment' },
  { value: 'gmdss',       label: 'GMDSS' },
  { value: 'medical',     label: 'Medical Equipment' },
  { value: 'special_ops', label: 'Special Operations' },
  { value: 'crew',        label: 'Crew' },
];

// Quick label lookup for display layers.
export const CATEGORY_LABELS = Object.fromEntries(
  CATEGORY_DEFINITIONS.map(({ value, label }) => [value, label])
);

// Normalises any legacy/inconsistent category value to its canonical code.
// Accepts enum KEYS ('LSA'), enum VALUES ('Life Saving Appliances'), arbitrary
// case ('lsa', 'LSA'), and free-text custom categories (returned as-typed
// lowercase).
export function normaliseCategory(category) {
  if (!category || typeof category !== 'string') return null;
  const trimmed = category.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();

  // 1. Already canonical code
  if (CATEGORY_LABELS[lower]) return lower;

  // 2. Enum KEY match (e.g. 'LSA' → 'lsa')
  const keyMatch = Object.keys(MAINTENANCE_CATEGORIES).find(k => k.toLowerCase() === lower);
  if (keyMatch) {
    // Map enum KEY to canonical code via the label match (handles 'SPECIAL_OPS' → 'special_ops')
    return keyMatch.toLowerCase();
  }

  // 3. Enum VALUE match (e.g. 'Life Saving Appliances' → 'lsa')
  const labelEntry = CATEGORY_DEFINITIONS.find(d => d.label.toLowerCase() === lower);
  if (labelEntry) return labelEntry.value;

  // 4. Free-text custom — preserve trimmed lowercase for filter consistency
  return lower;
}

// Resolves a stored category value to its human display label.
// Canonical → mapped label. Free-text custom → the value verbatim (already
// lowercase). For display use: callers should call this on `record.category`.
export function getCategoryLabel(category) {
  if (!category) return '';
  const lower = String(category).trim().toLowerCase();
  return CATEGORY_LABELS[lower] || category;  // fall through to original casing for free-text
}

// Auto-initialize on import (for backwards compatibility)
if (typeof window !== 'undefined') {
  initializeRegulations().catch(console.error);
}

export default {
  getMaintenanceSchedule,
  calculateMaintenanceDue,
  getMaintenanceCategories,
  switchCountry,
  MAINTENANCE_CATEGORIES
};